import { auditService } from '@/services';
import type { ListAuditActivitiesParams } from '@/services';
import { toaster } from '@/shared/ui/toaster.component';
import {
  downloadBlob,
  filenameFromResponse,
} from '@/shared/utils/download-blob.util';
import { useMutation } from '@tanstack/react-query';
import {
  AUDIT_EXPORT_MAX_ROWS,
  isExportTruncated,
  withUtf8Bom,
} from '../utils';

/**
 * Exporta a trilha do filtro atual em CSV e dispara o download.
 *
 * O CSV vem pronto do servidor (BOM UTF-8 e separador `;`, para o Excel pt-BR
 * não abrir "Ação" como "AÃ§Ã£o") e cobre o FILTRO INTEIRO, não a página
 * carregada na tela — por isso o arquivo pode ter muito mais linhas do que o
 * usuário está vendo.
 */
export function useAuditExport() {
  return useMutation({
    mutationFn: async (params: ListAuditActivitiesParams) => {
      const response = await auditService.exportActivities(params);

      if (!response.ok) {
        throw new Error(
          'Não foi possível exportar a trilha de auditoria agora. Tente novamente em instantes.'
        );
      }

      // Lemos como texto (e não `.blob()`) porque precisamos contar as linhas
      // quando o header de truncamento não chega — ver `isExportTruncated`.
      const csv = await response.text();
      const truncated = isExportTruncated(
        response.headers.get('X-Audit-Truncated'),
        csv
      );

      const filename = filenameFromResponse(response, 'auditoria.csv');
      downloadBlob(
        new Blob([withUtf8Bom(csv)], { type: 'text/csv;charset=utf-8' }),
        filename
      );

      return { filename, truncated };
    },
    onSuccess: ({ filename, truncated }) => {
      if (truncated) {
        toaster.create({
          type: 'warning',
          title: 'Exportação incompleta',
          description: `O arquivo ${filename} parou no teto de ${AUDIT_EXPORT_MAX_ROWS.toLocaleString('pt-BR')} linhas. Estreite o recorte (um dia por vez, ou uma ação específica) para levar tudo.`,
        });
        return;
      }

      toaster.create({
        type: 'success',
        title: 'Exportação concluída',
        description: `O arquivo ${filename} foi baixado.`,
      });
    },
    onError: (error: Error) => {
      toaster.create({
        type: 'error',
        title: 'Falha ao exportar',
        description: error.message,
      });
    },
  });
}
